import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { useAtomValue, useSetAtom } from 'jotai'
// @ts-ignore: No TS declaration present for a 10-year bundle
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'
import { useTranslation } from 'react-i18next'

import { Currency, type SearchSubmissionType } from '@/types'
import { getCheckoutPath } from '@/checkout-paths'
import CheckoutButton from '@/components/checkout-button'
import { ArrowForwardIcon } from '@/components/icons'
import LaterAvailabilityGuidance from '@/components/later-availability-guidance'
import SearchModal from '@/components/search-modal'
import MinimumStayNotice from '@/components/minimum-stay-notice'
import GarageDetailEndTime from '@/components/garage-detail-end-time'
import { getCurrency, getUpdatedSearchUrl } from '@/helpers'
import { searchSubmissionAtom } from '@/state'
import { useGarageSearch } from '@/hooks/use-garage-search'
import { useApplySuggestedStartTime } from '@/hooks/use-apply-suggested-start-time'
import { getGarageLaterAvailability } from '@/pwa/later-availability'
import { useGarageBookingDetails } from '@/hooks/use-garage-booking-details'
import { useElementHeight } from '@/hooks/use-element-height'
import { useLaterAvailabilityQuery } from '@/pwa/hooks/use-later-availability'

function formatPrice(cents: number | null, currency: Currency): string {
    if (cents == null) {
        return '---'
    }

    if (currency === Currency.eur) {
        return `~EUR ${cents / 100}`
    }

    return `${cents / 100}`
}

const desktopMediaQuery = '@media (min-width: 1230px)'
const sidebarMobileMediaQuery = '@media (max-width: 1229.98px)'

export default function GarageDetailSearch({
    garageId,
    garageAddress,
    showPopup,
    popupText,
}: {
    garageId: string,
    garageAddress: string,
    showPopup: boolean,
    popupText: string,
}) {
    const { t, i18n } = useTranslation()
    const userLang = i18n.language
    const searchSubmission = useAtomValue(searchSubmissionAtom)
    const setSearchSubmission = useSetAtom(searchSubmissionAtom)
    const currency = getCurrency()
    const { ref: bookingBarRef, height: bookingBarHeight } = useElementHeight(120)

    const {
        currentSearch,
        handleSelect,
        handleDateChange,
        handleFocusChange,
        mobileSearchDialog,
        setMobileSearchDialog,
        handleToggleMobileSearchDialog,
        desktopSearchDialog,
        setDesktopSearchDialog,
        handleToggleDesktopSearchDialog,
        handleSearchReset,
        reservationTimeRangeInvalid,
        reservationInThePast,
        reservationDays,
        reservationHours,
        selectedCETDateTime,
        currentCETDateTime,
        timeHighlight,
    } = useGarageSearch()

    const booking = useGarageBookingDetails(garageId, currency)
    const { isUnavailable, loading, parkingPeriod, showMinimumStay } = booking
    const effectiveEndsAt = showMinimumStay ? booking.effectiveEndsAt : null
    const { data: laterAvailabilityResponse } = useLaterAvailabilityQuery({
        enabled: isUnavailable,
    })
    const garageLaterAvailability = isUnavailable
        ? getGarageLaterAvailability(
            garageId,
            laterAvailabilityResponse?.garages ?? [],
            searchSubmission.startTime,
        )
        : null
    const applySuggestedStartTime = useApplySuggestedStartTime({
        urlVariant: 'reservation',
        garageId,
    })

    const checkoutHref = getCheckoutPath({
        isPwa: false,
        locale: i18n.resolvedLanguage || userLang,
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    })

    const handleSetSearch = (newSearch: SearchSubmissionType) => {
        setSearchSubmission(newSearch)
        setMobileSearchDialog(false)
        setDesktopSearchDialog(false)
        window.history.replaceState(
            {},
            '',
            getUpdatedSearchUrl({
                currentUrl: window.location.href,
                searchSubmission: newSearch,
                variant: 'reservation',
                garageId,
            }),
        )
    }

    const disabled = booking.disabled || reservationTimeRangeInvalid || reservationInThePast
    const availabilityErrors = booking.errors
    const frontendMobileErrors = [
        reservationInThePast ? t('search.startInThePast') : null,
        reservationTimeRangeInvalid ? t('search.invalidTimeRange') : null,
    ].filter((message): message is string => message !== null)
    const mobileErrors = availabilityErrors.length > 0 ? availabilityErrors : frontendMobileErrors
    const priceAmount = booking.amountCents
    const formattedPrice = formatPrice(priceAmount, currency)
    const formattedPriceLabel = currency === Currency.eur ? formattedPrice : `${formattedPrice} ${t('general.czk')}`
    const roundedPricePerHour = formatPrice(booking.hourlyAmountCents, currency)
    const roundedPricePerHourLabel = currency === Currency.eur ? roundedPricePerHour : `${roundedPricePerHour} ${t('general.czk')}`
    const ctaLabel = `${t('general.bookNowFor')} ${formattedPriceLabel}`
    const actionButtonSx = {
        fontSize: '15px',
        padding: '0.875rem 1.25rem',
        borderRadius: '7px',
        width: '100%',
    }
    const mobileActionButtonSx = {
        fontSize: '15px',
        padding: '0.875rem 1.25rem',
        borderRadius: '7px',
    }

    const renderActionButton = (fullWidth = true) => {
        const buttonSx = fullWidth ? actionButtonSx : mobileActionButtonSx

        if (isUnavailable || booking.isTimeRangeInvalid) {
            return (
                <Button
                    color="danger"
                    size="sm"
                    disabled
                    sx={{
                        ...buttonSx,
                        '&:disabled': {
                            opacity: 1,
                            color: 'white',
                            backgroundColor: 'danger.500',
                        },
                    }}
                >
                    {t(isUnavailable ? 'general.occupied' : 'general.cannotBook')}
                </Button>
            )
        }

        return (
            <CheckoutButton
                garageId={garageId}
                showPopup={showPopup}
                popupText={popupText}
                onSuccess={() => window.location.assign(checkoutHref)}
                label={ctaLabel}
                size="sm"
                loading={loading}
                disabled={disabled}
                sx={{
                    ...buttonSx,
                    backgroundColor: 'customBlack.main',
                    '&:hover': {
                        color: 'white',
                    },
                }}
            />
        )
    }

    return (
        <>
        <Box
            sx={{
                width: '100%',
                backgroundColor: 'common.white',
                border: '1px solid #E0E0E0',
                borderRadius: '10px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
                pb: 0,
                [desktopMediaQuery]: {
                    minHeight: 340,
                },
                [sidebarMobileMediaQuery]: {
                    border: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                },
            }}
        >
            <Box
                ref={bookingBarRef}
                sx={{
                    display: 'flex',
                    position: 'fixed',
                    width: '100vw',
                    minHeight: 120,
                    bottom: 0,
                    left: 0,
                    zIndex: 1041,
                    backgroundColor: 'common.white',
                    p: '10px 7px 22px',
                    borderTopWidth: '1px',
                    borderTopStyle: 'solid',
                    borderTopColor: 'var(--joy-palette-primary-300)',
                    flexDirection: 'column',
                    [desktopMediaQuery]: {
                        display: 'none',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        padding: '0.2rem 0.5rem',
                        pt: showMinimumStay ? 2 : undefined,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        color: 'customBlack.main',
                    }}
                    onClick={handleToggleMobileSearchDialog}
                >
                    <Box display="flex" flexGrow={1} gap={1} justifyContent="center">
                        <Box>
                            <b>{searchSubmission.startDate !== null ? moment(searchSubmission.startDate).locale(userLang).format('dd DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                            , {searchSubmission.startTime ?? '--:--'}
                        </Box>
                        <ArrowForwardRoundedIcon sx={{ color: 'customBlack.main', fontSize: '20px' }} />
                        <GarageDetailEndTime requestedDate={searchSubmission.endDate} requestedTime={searchSubmission.endTime} effectiveEndsAt={effectiveEndsAt} />
                    </Box>
                    <Box>
                        <EditIcon sx={{ color: 'customBlack.main', fontSize: '22px' }} />
                    </Box>
                </Box>
                {mobileErrors.map((message, index) =>
                    <Box
                        key={`${message}-${index}`}
                        sx={{
                            textAlign: 'center',
                            padding: '0rem 0.5rem 0.5rem',
                        }}
                    >
                        <Box color="red">{message}</Box>
                    </Box>
                )}
                {garageLaterAvailability &&
                    <LaterAvailabilityGuidance
                        availableTimes={garageLaterAvailability.availableTimes}
                        onSelectStartTime={applySuggestedStartTime}
                    />
                }
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 1,
                        gap: 1,
                    }}
                >
                    {showMinimumStay && parkingPeriod && <MinimumStayNotice parkingPeriod={parkingPeriod} compact />}
                    {renderActionButton(false)}
                </Box>
            </Box>

            <SearchModal
                searchModalOpen={mobileSearchDialog}
                handleToggleSearchModalOpen={handleToggleMobileSearchDialog}
                currentSearch={currentSearch}
                modalType="mobile"
                handleSearchReset={handleSearchReset}
                handleSelect={handleSelect}
                handleDateChange={handleDateChange}
                handleFocusChange={handleFocusChange}
                reservationDays={reservationDays}
                reservationHours={reservationHours}
                selectedCETDateTime={selectedCETDateTime}
                currentCETDateTime={currentCETDateTime}
                setSearch={handleSetSearch}
                garageDetail
                timeHighlight={timeHighlight}
            />

            <Box
                sx={{
                    display: 'none',
                    flexDirection: 'column',
                    background: 'white',
                    width: '100%',
                    [desktopMediaQuery]: {
                        display: 'flex',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem 0.5rem 0.75rem 1.5rem',
                        borderBottom: '1px solid var(--joy-palette-primary-200)',
                        fontSize: '1.125rem',
                    }}
                >
                    {garageAddress}
                    <Button
                        variant="soft"
                        onClick={handleToggleDesktopSearchDialog}
                        sx={{
                            color: 'black',
                            backgroundColor: 'var(--joy-palette-neutral-50)',
                            '&:hover': {
                                color: 'black',
                                backgroundColor: 'var(--joy-palette-neutral-100)',
                            },
                            borderRadius: 7,
                            border: '1px solid var(--joy-palette-neutral-200)',
                        }}
                    >
                        {t('search.edit')}
                    </Button>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto auto auto',
                        alignItems: 'center',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        flexGrow: 1,
                        justifyContent: 'space-between',
                        padding: '0.75rem 1.5rem',
                        borderBottom: '1px solid var(--joy-palette-primary-200)',
                        '&:hover': {
                            backgroundColor: 'secondary.100',
                        },
                    }}
                    onClick={handleToggleDesktopSearchDialog}
                >
                    <Box pr={2}>
                        <Box
                            sx={{
                                fontSize: '0.750rem',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: 'primary.500',
                            }}
                        >
                            {t('search.reservationStart')}
                        </Box>
                        <Box minWidth="8.25rem">
                            <b>{searchSubmission.startDate !== null ? moment(searchSubmission.startDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                            , {searchSubmission.startTime ?? '--:--'}
                        </Box>
                    </Box>
                    <ArrowForwardIcon color="#BDBDBD" />
                    <GarageDetailEndTime requestedDate={searchSubmission.endDate} requestedTime={searchSubmission.endTime} effectiveEndsAt={effectiveEndsAt} desktop />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        padding: '0.75rem 1.5rem',
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                fontSize: '0.750rem',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: 'primary.500',
                            }}
                        >
                            {t('search.reservationLength')}
                        </Box>
                        <Box>
                            {(reservationDays || reservationHours) ?
                                <Box color={reservationTimeRangeInvalid ? 'red' : 'inherit'} sx={{ textDecoration: showMinimumStay ? 'line-through' : undefined }}>
                                    <span>{reservationDays ? t('search.days', { count: reservationDays }) : ''}</span>
                                    <span>{reservationHours ? t('search.hours', { count: reservationHours }) : ''}</span>
                                </Box>
                            :
                                <Box color="red">{t('search.hours', { count: 0 })}</Box>
                            }
                        </Box>
                        {showMinimumStay && parkingPeriod &&
                            <Box color="customGreen.dark">{t('search.hours', { count: parkingPeriod.durationHours })}</Box>
                        }
                    </Box>
                    {showMinimumStay &&
                        <Typography
                            component="span"
                            sx={{
                                alignSelf: 'flex-end',
                                flexShrink: 0,
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                                color: '#0EC020',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                            }}
                        >
                            {formattedPriceLabel}
                        </Typography>
                    }
                    {!showMinimumStay && <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Box
                            sx={{
                                fontSize: '0.750rem',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: 'primary.500',
                            }}
                        >
                            {t('search.pricePerHour')}
                        </Box>
                        <Typography
                            component="span"
                            sx={{
                                color: 'inherit',
                            }}
                        >
                            {roundedPricePerHourLabel}
                        </Typography>
                    </Box>}
                </Box>
            </Box>

            <SearchModal
                searchModalOpen={desktopSearchDialog}
                handleToggleSearchModalOpen={handleToggleDesktopSearchDialog}
                currentSearch={currentSearch}
                modalType="desktop"
                handleSearchReset={handleSearchReset}
                handleSelect={handleSelect}
                handleDateChange={handleDateChange}
                handleFocusChange={handleFocusChange}
                reservationDays={reservationDays}
                reservationHours={reservationHours}
                selectedCETDateTime={selectedCETDateTime}
                currentCETDateTime={currentCETDateTime}
                setSearch={handleSetSearch}
                garageDetail
                timeHighlight={timeHighlight}
            />

            <Box
                sx={{
                    display: 'none',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'neutral.outlinedBorder',
                    [desktopMediaQuery]: {
                        display: 'block',
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {!showMinimumStay && <Box sx={{ textAlign: 'right' }}>
                        <Typography
                            component="span"
                            sx={{
                                color: '#0EC020',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                            }}
                        >
                            {formattedPriceLabel}
                        </Typography>
                    </Box>}
                    {showMinimumStay && parkingPeriod && <MinimumStayNotice parkingPeriod={parkingPeriod} />}
                    <Box>
                        {renderActionButton()}
                    </Box>
                    {garageLaterAvailability &&
                        <LaterAvailabilityGuidance
                            availableTimes={garageLaterAvailability.availableTimes}
                            onSelectStartTime={applySuggestedStartTime}
                        />
                    }
                </Box>
            </Box>
        </Box>
        <Box height={bookingBarHeight} sx={{ [desktopMediaQuery]: { display: 'none' } }} />
        {availabilityErrors.length > 0 &&
            <Box
                sx={{
                    zIndex: 1040,
                    mt: '1rem',
                    [sidebarMobileMediaQuery]: {
                        display: 'none',
                    },
                }}
            >
                {availabilityErrors.map((message, index) =>
                    <Box
                        key={`${message}-${index}`}
                        sx={{
                            maxWidth: '100%',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            textAlign: 'center',
                            color: 'common.white',
                            backgroundColor: '#E21C1C',
                            p: '0.5rem',
                            borderRadius: '10px',
                            mt: index > 0 ? '1px' : 0,
                        }}
                    >
                        {message}
                    </Box>
                )}
            </Box>
        }
        </>
    )
}
